import type { PaginationInfo } from '@/types';

export const COMPLETE_COLLECTION_PAGE_SIZE = 100;
export const MAX_COMPLETE_COLLECTION_PAGES = 1_000;

interface PaginatedPage<T> {
  items: T[];
  pagination: PaginationInfo;
}

/**
 * Fetch every page of a collection used by selectors and permission checks.
 *
 * The API caps pages at 100 records. Page metadata is validated and the page
 * count is bounded so a malformed response cannot create an infinite request
 * loop. Repeated IDs are merged while preserving their first-seen order.
 */
export async function fetchCompleteCollection<T extends { id: string }>(
  fetchPage: (page: number, limit: number) => Promise<PaginatedPage<T>>,
  options: { maxPages?: number; pageSize?: number } = {},
): Promise<T[]> {
  const maxPages = options.maxPages ?? MAX_COMPLETE_COLLECTION_PAGES;
  const pageSize = options.pageSize ?? COMPLETE_COLLECTION_PAGE_SIZE;
  if (!Number.isInteger(maxPages) || maxPages < 1) {
    throw new Error('Complete collection maxPages must be a positive integer');
  }
  if (!Number.isInteger(pageSize) || pageSize < 1) {
    throw new Error('Complete collection pageSize must be a positive integer');
  }

  const records = new Map<string, T>();
  let lastPage = 1;

  for (let page = 1; page <= lastPage; page += 1) {
    const response = await fetchPage(page, pageSize);
    const { pagination } = response;

    if (!Number.isInteger(pagination.page) || pagination.page !== page) {
      throw new Error(`Invalid pagination response for page ${page}`);
    }
    if (!Number.isInteger(pagination.totalPages) || pagination.totalPages < 0) {
      throw new Error('Invalid pagination totalPages');
    }
    if (pagination.totalPages > maxPages) {
      throw new Error(`Complete collection exceeds the ${maxPages}-page safety limit`);
    }

    lastPage = Math.max(lastPage, pagination.totalPages);
    for (const record of response.items) records.set(record.id, record);
  }

  return [...records.values()];
}
